/*
 * nco-product-card.js
 *
 * Interaction logic for snippets/nco-product-card.liquid:
 *   - Dot strip horizontal scroll with arrow indicators for overflow
 *   - Favourite toggle persisted to localStorage
 *
 * Loaded as an ES module via:
 *   <script src="{{ 'nco-product-card.js' | asset_url }}" type="module" defer></script>
 *
 * Standards:
 *   E5 — zero ESLint errors/warnings
 *   E7 — deferred, never blocks render
 *   S2 — vanilla JS, event delegation, no jQuery
 *   S5 — ARIA state kept in sync (aria-pressed on favourites)
 */

const STORAGE_KEY = 'nco-favourites';
const SCROLL_AMOUNT = 120;

const SELECTORS = {
  card: '[data-nco-product-card]',
  dotsWrap: '[data-nco-dots-wrap]',
  dots: '[data-nco-dots]',
  arrowLeft: '[data-nco-dots-arrow="left"]',
  arrowRight: '[data-nco-dots-arrow="right"]',
  favourite: '[data-nco-favourite]',
};

const STATE = {
  favouriteActive: 'nco-product-card__favourite--active',
  arrowHidden: 'nco-product-card__dots-arrow--hidden',
};

/* ------------------------------------------------------------------ */
/* Favourites                                                         */
/* ------------------------------------------------------------------ */

function getFavourites() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function setFavourites(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* localStorage full or blocked — degrade silently */
  }
}

function toggleFavourite(button) {
  const productId = button.getAttribute('data-product-id');
  if (!productId) return;

  const favs = getFavourites();
  const index = favs.indexOf(productId);

  if (index === -1) {
    favs.push(productId);
  } else {
    favs.splice(index, 1);
  }

  setFavourites(favs);
  const isActive = index === -1;
  button.classList.toggle(STATE.favouriteActive, isActive);
  button.setAttribute('aria-pressed', String(isActive));
}

function restoreFavourites() {
  const favs = getFavourites();
  if (favs.length === 0) return;

  const buttons = document.querySelectorAll(SELECTORS.favourite);
  buttons.forEach(function (btn) {
    const id = btn.getAttribute('data-product-id');
    if (favs.indexOf(id) !== -1) {
      btn.classList.add(STATE.favouriteActive);
      btn.setAttribute('aria-pressed', 'true');
    }
  });
}

/* ------------------------------------------------------------------ */
/* Dot strip scroll                                                   */
/* ------------------------------------------------------------------ */

function updateArrows(wrap) {
  const dots = wrap.querySelector(SELECTORS.dots);
  if (!dots) return;

  const left = wrap.querySelector(SELECTORS.arrowLeft);
  const right = wrap.querySelector(SELECTORS.arrowRight);
  if (!left || !right) return;

  const scrollLeft = dots.scrollLeft;
  const maxScroll = dots.scrollWidth - dots.clientWidth;
  const threshold = 2;

  const hideLeft = scrollLeft <= threshold;
  const hideRight = scrollLeft >= maxScroll - threshold;

  left.classList.toggle(STATE.arrowHidden, hideLeft);
  left.setAttribute('aria-hidden', String(hideLeft));
  left.tabIndex = hideLeft ? -1 : 0;

  right.classList.toggle(STATE.arrowHidden, hideRight);
  right.setAttribute('aria-hidden', String(hideRight));
  right.tabIndex = hideRight ? -1 : 0;
}

function initDotStrip(wrap) {
  const dots = wrap.querySelector(SELECTORS.dots);
  if (!dots) return;

  updateArrows(wrap);

  dots.addEventListener('scroll', function () {
    updateArrows(wrap);
  }, { passive: true });

  wrap.addEventListener('click', function (e) {
    const arrow = e.target.closest('[data-nco-dots-arrow]');
    if (!arrow) return;

    e.preventDefault();
    const direction = arrow.getAttribute('data-nco-dots-arrow');
    const delta = direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT;
    dots.scrollBy({ left: delta, behavior: 'smooth' });
  });
}

/* ------------------------------------------------------------------ */
/* Init                                                               */
/* ------------------------------------------------------------------ */

function init() {
  /* Restore saved favourites */
  restoreFavourites();

  /* Favourite toggle via event delegation on document */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest(SELECTORS.favourite);
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();
    toggleFavourite(btn);
  });

  /* Init dot strips */
  const wraps = document.querySelectorAll(SELECTORS.dotsWrap);
  wraps.forEach(initDotStrip);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
